import type { components } from "@brandex/api-client";
import { formatTaka } from "@brandex/i18n";
import { useBrandex } from "@brandex/dashboard";
import { Spinner } from "@brandex/ui";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { QueryError } from "../views";
import { COLUMNS } from "./pipeline";

type Overview = components["schemas"]["OverviewDto"];

const STAGE = Object.fromEntries(COLUMNS.map((c) => [c.status, c.label])) as Record<string, string>;

function Tile({ label, value, note }: { label: string; value: ReactNode; note?: string }) {
  return (
    <div className="glass rounded-[var(--radius-lg)] p-5">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-3xl font-bold tabular-nums">{value}</p>
      {note !== undefined ? <p className="mt-1 text-xs text-muted-foreground">{note}</p> : null}
    </div>
  );
}

function Attention({ label, value, to }: { label: string; value: number; to: string }) {
  const { nav } = useBrandex();
  return (
    <nav.Link href={to} className="flex items-center justify-between rounded-md border border-border px-4 py-3 text-sm hover:bg-muted">
      <span>{label}</span>
      <span className={`font-display text-xl font-bold tabular-nums ${value > 0 ? "text-coral-400" : "text-muted-foreground"}`}>{value}</span>
    </nav.Link>
  );
}

/**
 * Admin overview (F-16): clients, money (payments staff only), this month's
 * pipeline, what needs attention, and the next due items. Calculated by
 * BrandEx from its own records.
 */
export function OverviewPage() {
  const { api } = useBrandex();
  const o = useQuery<Overview>({
    queryKey: ["admin", "overview"],
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/overview");
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
    refetchInterval: 60_000,
  });
  if (o.isPending) return <Spinner label="Loading…" />;
  if (o.isError) return <QueryError error={o.error} />;
  const d = o.data;
  const money = (paisa: number) => formatTaka("en", paisa);
  return (
    <div className="grid gap-6">
      <header>
        <h1 className="font-display text-3xl font-bold tracking-tight">Overview</h1>
        <p className="mt-1 text-sm text-muted-foreground">Brandex operations, {d.period}. Calculated from platform records; refreshed every minute.</p>
      </header>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Active clients" value={d.active_clients} />
        {d.finance ? (
          <>
            <Tile label="Monthly recurring revenue" value={money(d.finance.mrr_paisa)} />
            <Tile
              label="Est. margin after cost and CAC"
              value={money(d.finance.estimated_margin_paisa)}
              note={`Component costs from the pricing sheet plus ${money(d.finance.cac_per_client_paisa)} CAC per client.`}
            />
          </>
        ) : null}
        <Tile label="In onboarding" value={d.in_onboarding} note="Package requested or quoted" />
      </div>

      <section aria-labelledby="pipeline-title" className="glass grid gap-3 rounded-[var(--radius-lg)] p-5">
        <h2 id="pipeline-title" className="font-semibold">Production pipeline, all clients</h2>
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          {d.pipeline.map((p) => (
            <li key={p.status} className="rounded-md border border-border p-3">
              <p className="text-xs text-muted-foreground">{STAGE[p.status]}</p>
              <p className="font-display text-2xl font-bold tabular-nums">{p.count}</p>
            </li>
          ))}
        </ul>
      </section>

      <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
        <section aria-labelledby="attention-title" className="grid content-start gap-2">
          <h2 id="attention-title" className="font-semibold">Needs attention</h2>
          <Attention label="Waiting for doctor approval" value={d.attention.waiting_for_doctor} to="/pipeline" />
          <Attention label="Booking requests pending" value={d.attention.bookings_pending} to="/bookings" />
          <Attention label="Payments due or overdue" value={d.attention.payments_due_or_overdue} to="/payments" />
          <Attention label="Changes requested" value={d.attention.changes_requested} to="/pipeline" />
        </section>
        <section aria-labelledby="due-title" className="grid content-start gap-2">
          <h2 id="due-title" className="font-semibold">Next due</h2>
          <div className="glass overflow-x-auto rounded-[var(--radius-lg)]">
            <table className="w-full min-w-[520px] text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr><th className="px-4 py-2">Item</th><th>Client</th><th>Status</th><th className="px-4">Due</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {d.next_due.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">Nothing due this month. Plan a month in the pipeline.</td></tr>
                ) : (
                  d.next_due.map((i) => (
                    <tr key={i.id}>
                      <td className="px-4 py-2 font-medium">{i.title}</td>
                      <td>{i.doctor_name ?? "—"}</td>
                      <td>{STAGE[i.status]}</td>
                      <td className="px-4">{i.due_on}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
